import { useMemo } from 'react';
import { useAppSelector } from '../../../../hooks/redux';
import { useLiveInventoryDisplay } from '../../../../hooks/useLiveInventoryDisplay';
import {
  buildLiveStockerIndex,
  liveStockerColumnSplit,
  lookupLiveStocker,
} from '../../../../features/simulation/liveStockerInventory';

export type StockerSlotKind = 'raw' | 'processed';

export type StockerInventoryVisuals = {
  totalOccupancy: number;
  rawCount: number;
  processedCount: number;
  /** Slot index → FOUP kind for rack colouring. */
  slotKinds: Map<number, StockerSlotKind>;
};

function buildSlotKinds(
  visibleCount: number,
  rawCount: number,
  processedCount: number
): Map<number, StockerSlotKind> {
  const kinds = new Map<number, StockerSlotKind>();
  const raw = Math.min(Math.max(0, rawCount), visibleCount);
  const processed = Math.min(Math.max(0, processedCount), Math.max(0, visibleCount - raw));
  for (let i = 0; i < raw; i += 1) kinds.set(i, 'raw');
  for (let offset = 0; offset < processed; offset += 1) {
    kinds.set(raw + offset, 'processed');
  }
  return kinds;
}

/**
 * Live inventory socket → rack FOUP colours (raw vs processed columns).
 */
export function useStockerInventoryVisuals(
  stockerId: string | undefined,
  visibleCount: number,
  aliases: readonly unknown[] = []
): StockerInventoryVisuals {
  const running = useAppSelector((state) => state.simulation.running);
  const source = useAppSelector((state) => state.simulation.source);
  const liveInventory = useAppSelector((state) => state.simulation.live.inventory);
  const displayInventory = useLiveInventoryDisplay();
  const inventoryForVisuals =
    source === 'live' && running ? (displayInventory ?? liveInventory) : liveInventory;
  const layoutElements = useAppSelector((state) => state.layout.elements);

  return useMemo(() => {
    const element = stockerId ? layoutElements[stockerId] : undefined;
    const liveStocker = lookupLiveStocker(
      buildLiveStockerIndex(inventoryForVisuals),
      stockerId ?? '',
      [
        element?.metadata.name,
        element?.customProperties?.stockerId,
        element?.customProperties?.amhsId,
        ...aliases,
      ]
    );
    const columns = liveStockerColumnSplit(liveStocker);
    const totalOccupancy =
      typeof liveStocker?.occupancy === 'number'
        ? liveStocker.occupancy
        : columns
          ? columns.raw + columns.processed
          : visibleCount;
    const fillCount = visibleCount;

    if (!columns) {
      return {
        totalOccupancy,
        rawCount: fillCount,
        processedCount: 0,
        slotKinds: buildSlotKinds(fillCount, fillCount, 0),
      };
    }

    const rawCount = Math.min(Math.max(0, columns.raw), fillCount);
    const processedCount = Math.min(
      Math.max(0, columns.processed),
      Math.max(0, fillCount - rawCount)
    );
    return {
      totalOccupancy,
      rawCount,
      processedCount,
      slotKinds: buildSlotKinds(fillCount, rawCount, processedCount),
    };
  }, [aliases, inventoryForVisuals, layoutElements, stockerId, visibleCount]);
}
